import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import { isHealthProductEligible, isLowStock } from "@/lib/health-products/eligibility";

function publicClient() {
  const url = process.env.SUPABASE_URL;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) throw new Error("supabase_env_missing");
  return createClient<Database>(url, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
          headers.delete("Authorization");
        }
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });
}

// Customer-safe columns only: no batch numbers, raw stock counts or internal notes.
const COLS =
  "id, code, slug, name, note, description, brand, product_type, category, image_url, alt_text, gallery_urls, price_paise, mrp_paise, gst_rate_percent, price_includes_gst, pack_size, net_quantity, unit, ingredients, directions, warnings, seo_title, seo_description, sort_order, is_active, is_demo, is_published, track_stock, stock_quantity, low_stock_threshold, ops_availability, is_purchasable";

export type PublicHealthProduct = {
  id: string;
  sku: string;
  slug: string;
  name: string;
  note: string;
  description: string | null;
  brand: string | null;
  productType: string | null;
  category: string | null;
  imageUrl: string;
  altText: string;
  gallery: string[];
  pricePaise: number;
  mrpPaise: number | null;
  gstRatePercent: number;
  priceIncludesGst: boolean;
  packSize: string | null;
  netQuantity: number | null;
  unit: string | null;
  ingredients: string | null;
  directions: string | null;
  warnings: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  lowStock: boolean;
};

type Row = Database["public"]["Tables"]["products"]["Row"];

function toPublic(r: Row): PublicHealthProduct {
  return {
    id: r.id,
    sku: r.code ?? "",
    slug: r.slug ?? "",
    name: r.name,
    note: r.note,
    description: r.description,
    brand: r.brand,
    productType: r.product_type,
    category: r.category,
    imageUrl: r.image_url ?? "",
    altText: r.alt_text?.trim() || r.name,
    gallery: r.gallery_urls ?? [],
    pricePaise: r.price_paise as number,
    mrpPaise: r.mrp_paise,
    gstRatePercent: Number(r.gst_rate_percent),
    priceIncludesGst: Boolean(r.price_includes_gst),
    packSize: r.pack_size,
    netQuantity: r.net_quantity == null ? null : Number(r.net_quantity),
    unit: r.unit,
    ingredients: r.ingredients,
    directions: r.directions,
    warnings: r.warnings,
    seoTitle: r.seo_title,
    seoDescription: r.seo_description,
    lowStock: isLowStock(r),
  };
}

const eligible = (r: Row) =>
  isHealthProductEligible({
    ...r,
    gst_rate_percent: r.gst_rate_percent == null ? null : Number(r.gst_rate_percent),
  });

/** Public listing. Throws on read errors so the page can show a real error state. */
export const listHealthProducts = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicHealthProduct[]> => {
    const { data, error } = await publicClient()
      .from("products")
      .select(COLS)
      .eq("is_active", true)
      .eq("is_published", true)
      .eq("is_demo", false)
      .order("sort_order", { ascending: true });
    if (error) {
      console.error("[health-products] list failed", error);
      throw new Error("health_products_unavailable");
    }
    return ((data ?? []) as unknown as Row[]).filter(eligible).map(toPublic);
  },
);

export const getHealthProduct = createServerFn({ method: "GET" })
  .inputValidator((raw: unknown) => z.object({ slug: z.string().min(1).max(120) }).parse(raw))
  .handler(async ({ data }): Promise<PublicHealthProduct | null> => {
    const { data: row, error } = await publicClient()
      .from("products")
      .select(COLS)
      .eq("slug", data.slug)
      .eq("is_active", true)
      .eq("is_published", true)
      .eq("is_demo", false)
      .maybeSingle();
    if (error) {
      console.error("[health-products] get failed", error);
      throw new Error("health_products_unavailable");
    }
    const r = row as unknown as Row | null;
    return r && eligible(r) ? toPublic(r) : null;
  });
